import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { toast } from '@rapoo/element'
import { useDeviceStore, useLocaleStore, useFirmwareStore } from '@/stores'
import { BaseDevice, ConnectionType } from '@rapoo/device-sdk'
import { defaultReportError } from '@/composables/useErrorLogger'
import { useLoading } from '../useLoading'
import { getFirmwareBaseUrl } from '@/utils/oss'
import { runFirmwareUpdateForDevice } from './runFirmwareUpdate'

type UpgradeInfo = {
  Date: string
  DescriptionZh: string
  DescriptionEn: string
  Version: string
}

export interface FirmwareUpgradeOptions {
  /** 升级固件时是否检查有线连接，默认 false */
  checkWired?: boolean
  /** 获取升级列表 model 字段的 key: 'mainControl' | 'dongleModel' */
  modelField?: 'mainControl' | 'dongleModel'
}

/** 固件升级核心业务逻辑
 *
 * ⚠️ 注意：该 hook 不负责判断"是否能升级"（canUpdate），
 * 因为 base/keyboard/mouse 各设备类型的判断逻辑不同。
 * 调用方需根据自身情况自行计算 canUpdate。
 * hook 提供 `currentDeviceCanUpdate`(number) 和 `currentDeviceCanUpdateFirmware`(VersionInfo[]) 供调用方使用。
 */
export function useFirmwareUpgrade(options: FirmwareUpgradeOptions = {}) {
  const { checkWired = false, modelField = 'mainControl' } = options

  const router = useRouter()
  const { t } = useLocaleStore()
  // 根据当前渠道（VITE_CHANNEL）选择国内/海外固件 base URL；不再依赖 VITE_UPGRADE_LIST_URL
  const upgradeBaseUrl = getFirmwareBaseUrl()

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const { isLoading, withLoading } = useLoading()

  const firmwareStore = useFirmwareStore()
  const {
    firmwareUpgradeProgress,
    isFirmwareUpdating,
    showUpgradeProgressDialog,
    currentDeviceCanUpdateFirmware,
    currentDeviceCanUpdate,
    currentVersions,
  } = storeToRefs(firmwareStore)

  // ========== 版本检查 ==========
  const isLatestVersionTipOpen = ref(false)
  const isFirmwareUpgradeOpen = ref(false)

  const latestVersionText = (canUpdate: boolean | undefined) => {
    return canUpdate === undefined
      ? t('setting.device.firmware.unsupportedOnlineUpdate')
      : t('setting.device.firmware.latestVersionTip')
  }

  const handleCheckVersion = async (canUpdate: boolean | undefined) => {
    if (!currentDevice.value) return
    withLoading(async () => {
      try {
        await firmwareStore.checkLatestVersion(currentDevice.value)
        if (canUpdate) {
          isFirmwareUpgradeOpen.value = true
        } else {
          isLatestVersionTipOpen.value = true
        }
      } catch (error) {
        console.error('Failed to check firmware version:', error)
        defaultReportError(`Failed to check firmware version: ${JSON.stringify(error)}`)
        isLatestVersionTipOpen.value = true
      }
    })
  }

  // ========== 固件升级 ==========
  /** 与 App 级 FirmwareUpgradeProgressDialog 共用 store 开关 */
  const upgradeDialog = showUpgradeProgressDialog

  const closeUpgradeDialog = () => {
    firmwareStore.unregisterUpgradeProgress()
    isFirmwareUpgradeOpen.value = false
    firmwareStore.closeUpgradeProgressDialog()
  }

  const openUpgradeDialog = () => {
    firmwareStore.unregisterUpgradeProgress()
    firmwareStore.registerUpgradeProgress()
    isFirmwareUpgradeOpen.value = false
    firmwareStore.openUpgradeProgressDialog()
  }

  /**
   * 对指定设备执行固件升级（APP 进 Boot 或 Boot 恢复同一入口）。
   * 首页点击 update 设备与设置页「升级」共用。
   */
  const executeFirmwareUpdate = async (targetDevice: BaseDevice) => {
    await runFirmwareUpdateForDevice(targetDevice, {
      router,
      failToast: t('toast.firmware.upgradeFailed'),
      upgradingToast: t('toast.firmware.upgrading'),
    })
  }

  const handleUpdateFirmware = async () => {
    if (!currentDevice.value) {
      console.error('当前无可用设备')
      return
    }

    if (checkWired && currentDevice.value.getConnectionType() !== ConnectionType.Wired) {
      toast.warning(t('setting.device.firmware.upgradeWiredTip'))
      return
    }

    // 先关「固件更新」确认窗，避免与进 Boot 授权弹窗叠层
    isFirmwareUpgradeOpen.value = false
    await executeFirmwareUpdate(currentDevice.value)
  }

  // ========== 更新日志 ==========
  const isUpgradeListOpen = ref(false)
  const upgradeList = ref<UpgradeInfo[]>([])

  const handleOpenUpgradeList = async () => {
    isUpgradeListOpen.value = true

    const file =
      modelField === 'dongleModel'
        ? import.meta.env.VITE_UPGRADE_DONGLE_LIST_FILE_NAME
        : import.meta.env.VITE_UPGRADE_LIST_FILE_NAME

    const model =
      modelField === 'dongleModel'
        ? (currentDevice.value?.manifest.dongleModel ?? '')
        : (currentDevice.value?.manifest.mainControl ?? '')

    const listUrl = `${upgradeBaseUrl}/${model}/${file}`

    withLoading(async () => {
      try {
        const res = await fetch(listUrl)
        if (!res.ok) {
          throw new Error('Network response was not ok')
        }
        const json = await res.json()
        upgradeList.value = json.Package as UpgradeInfo[]
      } catch (error) {
        console.error('Error fetching upgrade list:', error)
        defaultReportError(`Error fetching upgrade list: ${JSON.stringify(error)}`)
      }
    })
  }

  return {
    // 状态
    isLoading,
    isLatestVersionTipOpen,
    isFirmwareUpgradeOpen,
    upgradeDialog,
    isUpgradeListOpen,
    upgradeList,
    latestVersionText,
    firmwareUpgradeProgress,
    isFirmwareUpdating,
    currentDeviceCanUpdate,
    currentDeviceCanUpdateFirmware,
    currentVersions,
    // 操作
    handleCheckVersion,
    handleUpdateFirmware,
    executeFirmwareUpdate,
    handleOpenUpgradeList,
    closeUpgradeDialog,
    openUpgradeDialog,
  }
}
